import React, { useState } from 'react';
import styled from '@emotion/styled';
import { COLORS } from '@trustpilot/businessapp-patternlibrary/lib/constants';
import { Card, Checkbox, Input, Button, Heading, Body } from '@trustpilot/businessapp-patternlibrary';
import { connect } from 'react-redux';
import { LinkText, useTranslations } from 'ilenia';
import BillingInformationAlert from './BillingInformationAlert';
import * as analyticsActions from './actions/analytics';
import { shop as shopActions } from './actions';

interface BillingInfoAndConditionsProps {
  businessUnitDetails: { companyName: string };
  businessName: string;
  setBusinessName: (name: string) => void;
  isCreating: boolean;
  track: (path: string, props: object) => void;
  isMissingDetails: boolean;
  isPricingError: boolean;
  createOrder: () => void;
}

const BillingInfoAndConditions = ({
  businessUnitDetails,
  businessName,
  setBusinessName,
  isCreating,
  track,
  isMissingDetails,
  isPricingError,
  createOrder,
}: BillingInfoAndConditionsProps): JSX.Element => {
  const [translations] = useTranslations();
  const [isAccepted, setIsTermsAccepted] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const onToggleAccept = (): void => setIsTermsAccepted((prev) => !prev);

  const onBusinessNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setBusinessName(event.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setIsSubmitClicked(true);
    if (businessName.trim()) createOrder();
  };

  const onLinkClick = (): void => {
    track('Integration Link Clicked', {
      location: window.location.pathname,
      placement: 'Bottom middle',
      action: 'terms and conditions link clicked',
      name: 'terms-and-conditions-link-clicked',
    });
  };

  const isError = isMissingDetails || isPricingError;
  const isNextDisabled = !isAccepted || isCreating || isError;
  const errorMessage = isSubmitClicked && !businessName.trim() && translations['billingInfoAndConditions.legalNameOfBusinessIsRequiredField'];

  return (
    <StyledInfoAndConditionsContainer>
      <form id="terms-and-conditions-form" onSubmit={handleSubmit}>
        <StyledSubscriberInfo>
          <BillingInformationAlert />
          <StyledHeadingText>
            <Heading level="2">{translations['billingInfoAndConditions.subscriberInfo']}</Heading>
          </StyledHeadingText>
          <StyledDivider />
          <StyledInfoContainer>
            <StyledInfoLabelBlock>
              <StyledHeadingText>
                <Body level="2" weight="bold">
                  {translations['billingInfoAndConditions.companyDetails']}
                </Body>
              </StyledHeadingText>
            </StyledInfoLabelBlock>
            <StyledInfoInputBlock>
              <StyledInputContainer>
                <Input
                  size="medium"
                  label={translations['billingInfoAndConditions.companyName']}
                  value={businessUnitDetails.companyName}
                  disabled
                />
              </StyledInputContainer>
              <StyledInputContainer>
                <Input
                  id="legal-name-of-business"
                  size="medium"
                  label={translations['billingInfoAndConditions.legalNameOfBusiness']}
                  value={businessName}
                  onChange={onBusinessNameChange}
                  errorMessage={errorMessage}
                />
              </StyledInputContainer>
            </StyledInfoInputBlock>
          </StyledInfoContainer>
        </StyledSubscriberInfo>
        <StyledTermsAndConditions>
          <StyledHeadingText>
            <Heading level="2">{translations['billingInfoAndConditions.termsAndConditions']}</Heading>
          </StyledHeadingText>
          <StyledDivider />
          <StyledCheckboxContainer>
            <Checkbox id="terms-and-conditions-checkbox" checked={isAccepted} onChange={onToggleAccept}>
              <Body level="2" weight="normal">
                <LinkText
                  id="billingInfoAndConditions.iAcceptTermsAndConditions"
                  links={[{
                    href: process.env.REACT_APP_LegalUrl,
                    onClick: onLinkClick,
                    target: '_blank',
                  }]}
                />
              </Body>
            </Checkbox>
          </StyledCheckboxContainer>
        </StyledTermsAndConditions>
        <StyledSubmitButtonContainer>
          <Button
            appearance="primary"
            size="large"
            disabled={isNextDisabled}
          >
            {translations['billingInfoAndConditions.next']}
          </Button>
        </StyledSubmitButtonContainer>
      </form>
    </StyledInfoAndConditionsContainer>
  );
};

const StyledSubmitButtonContainer = styled.div({
  float: 'right',
});

const StyledCheckboxContainer = styled.div({
  padding: '17px 0px',
});

const StyledInputContainer = styled.div({
  paddingBottom: '15px',
});

const StyledHeadingText = styled.span({
  color: 'rgb(27, 27, 33)',
  letterSpacing: '0.2px',
});

const StyledInfoContainer = styled.div({
  paddingTop: '20px',
  display: 'flex',
});

const StyledInfoLabelBlock = styled.div({
  width: '240px',
});

const StyledInfoInputBlock = styled.div({
  flexGrow: 2,
});

const StyledInfoAndConditionsContainer = styled.div({
  padding: '0px !important',
  height: '100%',
});

const StyledSubscriberInfo = styled(Card)({
  padding: '12px 31px 17px 25px',
  marginBottom: '17px',
});

const StyledTermsAndConditions = styled(Card)({
  padding: '14px 31px 8px 26px',
  marginBottom: '15px',
});

const StyledDivider = styled.div({
  backgroundColor: COLORS.grays.cardBorder,
  height: 1,
  marginTop: 8,
});

const mapStateToProps = (state: any) => ({
  businessName: state.shop.businessName,
  businessUnitDetails: state.businessUnit.details,
  isCreating: state.shop.isCreating,
  isMissingDetails: state.shop.isMissingDetails,
  isPricingError: state.pricing.isPricingError,
});

const mapDispatchToProps = (dispatch: any) => ({
  setBusinessName: (businessName: string) => dispatch(shopActions.setBusinessName(businessName)),
  createOrder: () => dispatch(shopActions.createOrder()),
  track: (name: string, data: any) => dispatch(analyticsActions.track(name, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BillingInfoAndConditions);
